import * as React from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DownloadIcon from "@mui/icons-material/Download";
import Box from "@mui/material/Box";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Button from "@mui/material/Button";
import { Stack } from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import "../App.css";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getInstituteConfig } from "../utility/instituteConfig";

export default function PaymentSummary({ props }) {
  const {
    studentId,
    studentName,
    refundBalance,
    gradeName,
    academicYearId,
    gradeId,
    pendingFees,
    feesSlabId,
    setRefundBalance,
    sectionName,
    sectionId,
  } = props;

  const [data, setData] = React.useState({
    fees_payment: {
      total_paid: 0,
      total_pending: 0,
      entries: [],
    },
    store_payment: {
      total_paid: 0,
      total_pending: 0,
      entries: [],
    },
    kit_payment: {
      total_paid: 0,
      total_pending: 0,
      entries: [],
    },
    transport_payment: {
      total_paid: 0,
      total_pending: 0,
      entries: [],
    },
    annual_subscription_payment: {
      total_paid: 0,
      total_pending: 0,
      entries: [],
    },
  });

  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("payment_date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const navigate = useNavigate();
  const instituteId = localStorage.getItem("instituteId");
  useEffect(() => {
    const getFeesReport = async () => {
      fetch(
        process.env.REACT_APP_JHS_DEVELOPMENT + "/feesReport/getFeesReport",
        {
          method: "POST",
          headers: { "Content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            academic_year_id: academicYearId,
            grade_id: gradeId ? gradeId : null,
            fees_slab_id: feesSlabId ? feesSlabId : null,
            section_id: sectionId ? sectionId : null,
            pending_fees: pendingFees ? true : false,
            // Narrows the DB query to just this student instead of asking
            // for (and decrypting) the entire matching roster to find one
            // row.
            student_id: studentId,
          }),
        },
      )
        .then((response) => response.json())
        .then((response) => {
          const targetStudentId = studentId; // Replace with the actual student ID you're checking for

          // getFeesReport now returns { rows, total } instead of a bare
          // array (added for pagination); unwrap .rows here.
          const rows = Array.isArray(response) ? response : response?.rows || [];

          // Find the student whose studentId matches the target
          const matchingStudent = rows.find(
            (student) => student.student_id === targetStudentId,
          );

          if (matchingStudent) {
            const updatedRefund = matchingStudent.refund_amount;

            if (updatedRefund !== undefined) {
              setRefundBalance((prev) => {
                return updatedRefund;
              });
            } else {
              console.error(
                "current_pending_amount not found for the matched student.",
              );
            }
          } else {
            console.error("No student found with the specified studentId.");
          }
        });
    };
    getFeesReport();
  }, [academicYearId, gradeId, feesSlabId, pendingFees]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_JHS_DEVELOPMENT +
            "/paymentSummary/getpaymentSummary",
          {
            method: "POST",
            headers: { "Content-type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              student_id: studentId,
              academic_year_id: academicYearId,
            }),
          },
        );
        if (response.status === 401) {
          console.log("Unauthorized access, redirecting to Sign-In...");
          navigate("/signin"); // Redirect if unauthorized
          return;
        }
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        console.log("API Response:", result); // Check the response structure

        // Access the payments data
        setData({
          fees_payment:
            result.fn_get_payment_summary_by_student_id_academic_year_id
              .fees_payment || [],
          store_payment:
            result.fn_get_payment_summary_by_student_id_academic_year_id
              .store_payment || [],
          kit_payment:
            result.fn_get_payment_summary_by_student_id_academic_year_id
              .kit_payment || [],
          transport_payment:
            result.fn_get_payment_summary_by_student_id_academic_year_id
              .transport_payment || [],
          annual_subscription_payment:
            result.fn_get_payment_summary_by_student_id_academic_year_id
              .annual_subscription_payment || [],
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [studentId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Get all the payment entries except for fees_payment, which we handle separately
  const allPayments = [
    // Handle "Fee Payment" separately to include only one entry
    ...(data.fees_payment.entries?.length
      ? [
          {
            ...data.fees_payment.entries[data.fees_payment.entries.length - 1], // Take the first entry (or customize based on your needs)
            payment_type: "Fee Payment",
            total_paid: data.fees_payment.total_paid,
            total_pending: data.fees_payment.total_pending,
          },
        ]
      : []), // Include only if there are any entries

    // Process "Store Payment"
    ...(data.store_payment.entries?.length
      ? [
          {
            ...data.store_payment.entries[
              data.store_payment.entries.length - 1
            ], // Take the first entry (or customize based on your needs)
            payment_type: "Store Payment",
            total_paid: data.store_payment.total_paid,
            total_pending: data.store_payment.total_pending,
          },
        ]
      : []), // Include only if there are any entries

    // Process "Transport Payment"
    ...(data.kit_payment.entries?.length
      ? [
          {
            ...data.kit_payment.entries[data.kit_payment.entries.length - 1], // Take the first entry (or customize based on your needs)
            payment_type: "Kit Payment",
            total_paid: data.kit_payment.total_paid,
            total_pending: data.kit_payment.total_pending,
          },
        ]
      : []), // Include only if there are any entries

    // Process "Transport Payment"
    ...(data.transport_payment.entries?.length
      ? [
          {
            ...data.transport_payment.entries[
              data.transport_payment.entries.length - 1
            ], // Take the first entry (or customize based on your needs)
            payment_type: "Transport Payment",
            total_paid: data.transport_payment.total_paid,
            total_pending: data.transport_payment.total_pending,
          },
        ]
      : []), // Include only if there are any entries

    // Process "Annual Subscription Payment"
    ...(data.annual_subscription_payment.entries?.length
      ? [
          {
            ...data.annual_subscription_payment.entries[
              data.annual_subscription_payment.entries.length - 1
            ], // Take the first entry (or customize based on your needs)
            payment_type: "Annual Subscription Payment",
            total_paid: data.annual_subscription_payment.total_paid,
            total_pending: data.annual_subscription_payment.total_pending,
          },
        ]
      : []), // Include only if there are any entries
  ];
  const sortedRows = allPayments
    .map((row, index) => ({ ...row, index: index + 1 }))
    .sort(getComparator(order, orderBy));

  // Calculate totals for amount paid and amount pending
  const totalAmountPaid = sortedRows.reduce(
    (acc, row) => acc + row.total_paid,
    0,
  );

  // Calculate total amount pending
  const totalAmountPending = sortedRows.reduce(
    (acc, row) => acc + row.total_pending,
    0,
  );

  const formatrefundBalance = refundBalance ? refundBalance : 0;

  const handleDownload = (row) => {
    const isTableDataEmpty =
      !allPayments ||
      allPayments.length === 0 ||
      allPayments.every(
        (payment) =>
          !payment.payment_type &&
          !payment.total_paid &&
          !payment.total_pending &&
          !payment.payment_mode &&
          !payment.payment_date &&
          !payment.description,
      );

    if (isTableDataEmpty) {
      return;
    }

    const institute = getInstituteConfig(instituteId);

    const doc = new jsPDF();

    // Header background
    doc.setFillColor("#0A64A2");
    const headerHeight = 20;
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), headerHeight, "F");

    // Logo
    if (institute.logo) {
      doc.addImage(institute.logo, "PNG", 9, 1, 28, 18);
    }

    // School Name
    const textStartY = 11;
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(institute.name, doc.internal.pageSize.getWidth() / 2, textStartY, {
      align: "center",
    });

    // Address
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.text(
      institute.address1,
      doc.internal.pageSize.getWidth() / 2,
      textStartY + 14,
      { align: "center" },
    );
    doc.text(
      institute.address2,
      doc.internal.pageSize.getWidth() / 2,
      textStartY + 18,
      { align: "center" },
    );
    if (institute.tel) {
      doc.text(
        institute.tel,
        doc.internal.pageSize.getWidth() / 2,
        textStartY + 22,
        { align: "center" },
      );
    }
    // Add Receipt Title, Number, and Date - Right aligned
    const receiptTextY = textStartY + 36; // Position the receipt content slightly lower
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(
      "PAYMENT SUMMARY REPORT",
      doc.internal.pageSize.getWidth() / 2,
      receiptTextY,
      { align: "center" },
    ); // Center aligned title // Center aligned title
    doc.setFontSize(10);

    let studentDetailsY = receiptTextY + 10; // Adjust Y position after receipt info

    // Set font for student name and details
    doc.setFont("helvetica", "bold");
    const studentLabelX = 14;

    // Constants for padding and maximum widths
    const PADDING = 5; // Uniform padding between elements
    const MAX_NAME_WIDTH = 70; // Max width for student name before wrapping

    // Handle long student name
    const studentname = `${studentName || ""}`;
    const studentNameWidth = doc.getTextWidth(studentname);
    const splitStudentName =
      studentNameWidth > MAX_NAME_WIDTH
        ? doc.splitTextToSize(studentname, MAX_NAME_WIDTH)
        : studentname;

    doc.text(`Name of Student: `, studentLabelX, studentDetailsY); // Left aligned
    doc.setFont("helvetica", "normal");
    doc.text(splitStudentName, studentLabelX + 30, studentDetailsY); // Adjust X position for student name

    // Dynamically calculate X position for Grade based on student name
    const studentNameActualWidth = Math.min(studentNameWidth, MAX_NAME_WIDTH); // Use actual width or max width
    const gradeX = studentLabelX + 30 + studentNameActualWidth + PADDING;

    // Grade
    doc.setFont("helvetica", "bold");
    doc.text(`Grade:`, gradeX, studentDetailsY);
    const gradeLabelWidth = doc.getTextWidth(`Grade: `);
    doc.setFont("helvetica", "normal");
    const gradeValue = `${gradeName || ""}`;
    doc.text(gradeValue, gradeX + gradeLabelWidth, studentDetailsY);

    // Dynamically calculate X position for Section based on Grade
    const gradeValueWidth = doc.getTextWidth(gradeValue);
    const sectionX = gradeX + gradeLabelWidth + gradeValueWidth + PADDING;

    // Section
    doc.setFont("helvetica", "bold");
    doc.text(`Section:`, sectionX, studentDetailsY);
    const sectionLabelWidth = doc.getTextWidth(`Section: `);
    doc.setFont("helvetica", "normal");
    const sectionValue = `${sectionName || ""}`;
    const sectionValueWidth = doc.getTextWidth(sectionValue);

    // Check if Section exceeds page width and adjust
    if (
      sectionX + sectionLabelWidth + sectionValueWidth >
      doc.internal.pageSize.width
    ) {
      // Move to the next line if it exceeds the page width
      studentDetailsY += 10; // Adjust Y position
      doc.text(`Section:`, studentLabelX, studentDetailsY); // Start from the left
      doc.text(
        sectionValue,
        studentLabelX + sectionLabelWidth,
        studentDetailsY,
      );
    } else {
      // Place Section normally
      doc.text(sectionValue, sectionX + sectionLabelWidth, studentDetailsY);
    }

    // Date
    const currentDate = new Date();
    const formattedDate = `${String(currentDate.getDate()).padStart(2, "0")}/${String(currentDate.getMonth() + 1).padStart(2, "0")}/${currentDate.getFullYear()}`;
    doc.setFont("helvetica", "bold");
    doc.text(`Date: `, 169, studentDetailsY);
    doc.setFont("helvetica", "normal");
    doc.text(`${formattedDate}`, 178, studentDetailsY);

    // Continue with table data, etc...
    const tableData = allPayments.map((row) => {
      const cleanedAmountPaid = row.total_paid;
      const cleanedAmountPending = row.total_pending;

      const amountPaid = parseFloat(cleanedAmountPaid) || 0;
      const amountPending = parseFloat(cleanedAmountPending) || 0;

      return [
        // row.student_id,
        row.payment_type,
        amountPaid.toLocaleString(),
        amountPending.toLocaleString(),
        row.payment_mode,
        new Date(row.payment_date).toLocaleDateString(),
        row.description,
      ];
    });

    const totalPaid = parseFloat(totalAmountPaid) || 0;
    const totalPending = parseFloat(totalAmountPending) || 0;
    tableData.push([
      "", // Empty for Student ID
      "", // Label for the totals
      "", // Empty for Fees Slab
      "", // Empty for Parent Name
      "", // Grand total label
      "", // Total Amount Paid
      "", // Total Amount Pending
    ]);
    tableData.push([
      "Grand Total",
      totalPaid.toLocaleString(),
      totalPending.toLocaleString(),
      "",
      "",
      `Refund Balance:${refundBalance}`,
    ]);

    // Table settings
    doc.autoTable({
      head: [
        [
          // "Student ID",
          "Payment Type",
          "Amount Paid",
          "Amount Pending",
          "Payment Mode",
          "Payment Date",
          "Description",
        ],
      ],
      body: tableData,
      startY: receiptTextY + 13, // Adjusted based on dynamic content
      styles: { fontSize: 8, halign: "center" },
      headStyles: {
        fillColor: "#0A64A2", // Blue header
        textColor: [255, 255, 255], // White text
        alignItems: "center",
      },
      theme: "grid",
      // Make the last row (Grand Total) bold
      didParseCell: (data) => {
        if (data.row.index === tableData.length - 1) {
          // Check if it's the last row
          data.cell.styles.fontStyle = "bold"; // Set font style to bold
        }
      },
    });

    // Save the PDF
    doc.save(
      `payment_summary_report_${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  };

  return (
    <div>
      <div style={{ marginBottom: "6px" }}>
        <Stack direction="row">
          <div className="main_title">Payment Summary of {studentName} </div>
          <Box className="download-report-box">
            <Button
              className="save_update_btn"
              width={20}
              variant="contained"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
            >
              Download Report
            </Button>
          </Box>
        </Stack>
      </div>
      <Box className="table-container">
        <Paper className="table-paper">
          <TableContainer component={Paper}>
            <Table aria-label="custom pagination table">
              <TableHead className="table-header">
                <TableRow>
                  <TableCell
                    className="table-cell"
                    align="center"
                    style={{ paddingLeft: "40px" }}
                  >
                    <TableSortLabel
                      className="table-sort-label "
                      active={orderBy === "payment_type"}
                      direction={orderBy === "payment_type" ? order : "desc"}
                      onClick={() => handleRequestSort("payment_type")}
                    >
                      Payment Type
                      {orderBy === "payment_type" ? (
                        <span style={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    className="table-cell"
                    align="center"
                    style={{ paddingLeft: "40px" }}
                  >
                    <TableSortLabel
                      className="table-sort-label "
                      active={orderBy === "amount_paid"}
                      direction={orderBy === "amount_paid" ? order : "desc"}
                      onClick={() => handleRequestSort("amount_paid")}
                    >
                      Amount Paid
                      {orderBy === "amount_paid" ? (
                        <span style={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    className="table-cell"
                    align="center"
                    style={{ paddingLeft: "40px" }}
                  >
                    <TableSortLabel
                      className="table-sort-label "
                      active={orderBy === "pending_amount"}
                      direction={orderBy === "pending_amount" ? order : "desc"}
                      onClick={() => handleRequestSort("pending_amount")}
                    >
                      Amount Pending
                      {orderBy === "pending_amount" ? (
                        <span style={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    className="table-cell"
                    align="center"
                    style={{ paddingLeft: "30px" }}
                  >
                    <TableSortLabel
                      className="table-sort-label "
                      active={orderBy === "payment_mode"}
                      direction={orderBy === "payment_mode" ? order : "desc"}
                      onClick={() => handleRequestSort("payment_mode")}
                    >
                      Payment Mode
                      {orderBy === "payment_mode" ? (
                        <span style={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" className="table-cell">
                    <TableSortLabel
                      className="table-sort-label "
                      style={{ marginLeft: "10px" }}
                      active={orderBy === "payment_date"}
                      direction={orderBy === "payment_date" ? order : "desc"}
                      onClick={() => handleRequestSort("payment_date")}
                    >
                      Payment Date
                      {orderBy === "payment_date" ? (
                        <span style={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    className="table-cell"
                    align="center"
                    style={{ paddingLeft: "30px" }}
                  >
                    <TableSortLabel
                      className="table-sort-label "
                      active={orderBy === "description"}
                      direction={orderBy === "description" ? order : "desc"}
                      onClick={() => handleRequestSort("description")}
                    >
                      Description
                      {orderBy === "description" ? (
                        <span style={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <StyledTableRow key={row.payment_id}>
                      <StyledTableCell align="center">
                        {row.payment_type}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.total_paid}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.total_pending}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.payment_mode}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {new Date(row.payment_date).toLocaleDateString()}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.description}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}

                <StyledTableRow>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell></StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell
                    align="center"
                    style={{
                      paddingLeft: "0px",
                      color: "#494747",
                      fontWeight: "bold",
                    }}
                  >
                    Grand Total:{" "}
                  </StyledTableCell>

                  <StyledTableCell
                    align="center"
                    style={{ color: "#494747", fontWeight: "bold" }}
                  >
                    {totalAmountPaid}
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    style={{ color: "#494747", fontWeight: "bold" }}
                  >
                    {totalAmountPending}
                  </StyledTableCell>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell> </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    style={{ color: "#494747", fontWeight: "bold" }}
                  >
                    Refund Balance: {formatrefundBalance}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{
                      backgroundColor: "#0A64A2",
                      color: "white",
                      width: "100%",
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    colSpan={7}
                    count={sortedRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </div>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
